import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Breadcrumbs, Chip, Stack, Typography } from '@mui/material';
import { ChevronLeft, ChevronRight, ExternalLink, Eye } from 'lucide-react';
import { ADMIN_PAGES_KEYS, NS } from '../../../../i18n/constants/keys';

export interface JobOpeningBreadcrumbProps {
  jobTitle: string;
  jobReferenceNumber: string;
  jobOpeningId: string;
}

export const JobOpeningBreadcrumb = ({ jobTitle, jobReferenceNumber, jobOpeningId }: JobOpeningBreadcrumbProps) => {
  const { t } = useTranslation(NS.adminPages);

  const displayTitle = jobTitle?.trim() || t(ADMIN_PAGES_KEYS.jobOpenings.nav.newJobOpening);
  const displayRef = jobReferenceNumber || `#${jobOpeningId}` || 'Pending';
  const previewUrl = jobOpeningId ? `/jobs/${jobOpeningId}` : '/jobs';

  return (
    <Box
      component="nav"
      aria-label="Breadcrumb"
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
        justifyContent: 'space-between',
        gap: 1.5,
        borderBottom: 1,
        borderColor: 'grey.100',
        px: 2,
        py: 1,
      }}
    >
      {/* Back link */}
      <Link to="/job-openings-list" style={{ textDecoration: 'none' }}>
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{
            color: 'text.secondary',
            fontSize: '0.75rem',
            fontWeight: 600,
            transition: 'color 0.2s',
            '&:hover': { color: 'primary.main' },
            '&:hover .back-chevron': { transform: 'translateX(-2px)' },
          }}
        >
          <ChevronLeft size={14} className="back-chevron" style={{ transition: 'transform 0.2s' }} />
          <Typography variant="caption" fontWeight={600}>
            {t(ADMIN_PAGES_KEYS.jobOpenings.nav.back)}
          </Typography>
        </Stack>
      </Link>

      {/* Job title + Reference */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ minWidth: 0 }}>
        <Typography
          variant="subtitle2"
          noWrap
          sx={{ fontWeight: 700, color: 'text.primary', maxWidth: { xs: '100%', sm: '42vw', lg: 520 } }}
        >
          {displayTitle}
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          label={
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', fontSize: '0.55rem' }}>
                {t(ADMIN_PAGES_KEYS.jobOpenings.nav.ref)}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.dark', fontSize: '0.6rem' }}>
                {displayRef}
              </Typography>
            </Stack>
          }
          sx={{ height: 22, borderColor: 'grey.200', bgcolor: 'grey.50' }}
        />
      </Stack>

      {/* Public Preview Button */}
      <Box
        component={Link}
        to={previewUrl}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          textDecoration: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.5,
          py: 0.5,
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: 'grey.200',
          bgcolor: 'background.paper',
          color: 'text.secondary',
          fontSize: '0.75rem',
          fontWeight: 600,
          transition: 'all 0.15s ease',
          '&:hover': {
            borderColor: 'primary.main',
            color: 'primary.main',
            bgcolor: 'primary.50',
            '& .link-icon': { transform: 'translateX(2px)' },
          },
        }}
      >
        <Eye size={13} />
        <span>Public Preview</span>
        <ExternalLink size={12} className="link-icon" style={{ transition: 'transform 0.2s' }} />
      </Box>
    </Box>
  );
};
